import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Simple swipeable image lightbox.
 * Props:
 *   - photos: string[]   full-resolution image URLs
 *   - startIndex: number
 *   - onClose: fn
 */
export default function PhotoLightbox({ photos, startIndex = 0, onClose }) {
    const [idx, setIdx] = useState(startIndex);
    const [touchStart, setTouchStart] = useState(null);

    const prev = useCallback(() => setIdx(i => (i === 0 ? photos.length - 1 : i - 1)), [photos.length]);
    const next = useCallback(() => setIdx(i => (i === photos.length - 1 ? 0 : i + 1)), [photos.length]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        // Prevent body scroll while open
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose, prev, next]);

    if (!photos || photos.length === 0) return null;

    const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchStart == null) return;
        const dx = e.changedTouches[0].clientX - touchStart;
        if (dx > 50) prev();
        else if (dx < -50) next();
        setTouchStart(null);
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
            data-testid="photo-lightbox"
            onClick={onClose}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <button
                data-testid="lightbox-close-btn"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label="Close"
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                <X size={22} />
            </button>

            {photos.length > 1 && (
                <>
                    <button
                        data-testid="lightbox-prev-btn"
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        aria-label="Previous"
                        className="absolute left-2 sm:left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                        <ChevronLeft size={26} />
                    </button>
                    <button
                        data-testid="lightbox-next-btn"
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        aria-label="Next"
                        className="absolute right-2 sm:right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
                        <ChevronRight size={26} />
                    </button>
                </>
            )}

            <img
                src={photos[idx]}
                alt=""
                onClick={(e) => e.stopPropagation()}
                className="max-h-[90vh] max-w-[92vw] object-contain select-none"
                data-testid="lightbox-image"
            />

            {photos.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium bg-black/40 rounded-full px-3 py-1">
                    {idx + 1} / {photos.length}
                </div>
            )}
        </div>
    );
}
